import React, { Fragment, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { AlertTriangle, Download, X } from 'lucide-react';
import {
  formatRemittanceCurrency,
  formatRemittanceDate,
  formatRemittanceDateRange,
} from '../../components/billing/remittance/remittanceFormatters';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  exportRowsToXlsx,
  exportSheetsToXlsx,
  type XlsxSheet,
} from '../../reporting/exportExcel';
import {
  billingGetArRollForward,
  billingGetArRollForwardLines,
} from '../../services/api/sdk.gen';
import type {
  ArRollForwardLine,
  ArRollForwardRow,
  BillingGetArRollForwardLinesData,
} from '../../services/api/types.gen';
import { billingKeys } from '../../services/queryClient';
import { useStore } from '../../store';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { parseISOToLocalDate } from '../../utils/dateUtils';

type LinesQuery = BillingGetArRollForwardLinesData['query'];
type Line = NonNullable<LinesQuery['line']>;
type Figures = Record<
  Exclude<Line, 'cash_received' | 'unapplied_cash'>,
  string
>;

const COLUMNS: {
  line: keyof Figures;
  label: string;
  note?: string;
  memo?: boolean;
}[] = [
  { line: 'opening', label: 'Opening' },
  {
    line: 'billed',
    label: 'Billed',
    note: 'expected revenue, or charges if none',
  },
  { line: 'payments', label: 'Payments' },
  { line: 'write_offs', label: 'Write-offs' },
  {
    line: 'payer_variance',
    label: 'Payer variance',
    note: 'tolerance and credit-balance adjustments',
  },
  { line: 'removed', label: 'Removed' },
  { line: 'unexplained', label: 'Unexplained' },
  { line: 'closing', label: 'Closing' },
  { line: 'billed_at_charges', label: 'Billed at charges', memo: true },
  { line: 'activity_outside_ar', label: 'Activity outside AR', memo: true },
];
const SPAN = COLUMNS.length + 3;
const LINE_LABELS: Partial<Record<Line, string>> = Object.fromEntries(
  COLUMNS.map(c => [c.line, c.label])
);

const CASH_COLUMNS = [
  { line: 'cash_received', label: 'Cash received' },
  { line: 'unapplied_cash', label: 'Unapplied cash' },
] as const;

const PAYER_LABELS = { MEDICARE: 'Medicare', OTHER: 'Other' } as const;

const monthLabel = (month: string): string =>
  parseISOToLocalDate(`${month}-01`)?.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  }) ?? month;

const ALL_MONTHS = 'all';

// The last closed month: the month before the report's as-of date, which is
// the server's business day, so the default matches the report's open month.
const previousMonth = (asOf: string): string => {
  const [year, month] = asOf.split('-').map(Number);
  return month === 1
    ? `${year - 1}-12`
    : `${year}-${String(month - 1).padStart(2, '0')}`;
};

const cellClass = 'px-2 py-1 text-right tabular-nums whitespace-nowrap';

// The shared export writes a currency cell only for a money column key, so
// money columns use a `_sum_balance` key and columnLabels holds the header.
const moneyKey = (key: string) => `${key}_sum_balance`;

const slug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// One open drill-down: the button's label plus the parts of its heading (line,
// month, and clinic and payer class, Total or Cash).
type Drill = { label: string; line: string; month: string; scope: string[] };

// The drill-down lines table and its export share these columns. `value` is the
// raw cell; a money column shows as currency and exports as a number. Amount
// stays last: the Lines total row spans every column before it. A column with
// `lines` shows only on those drill-downs; the Lines sheet has every column.
const LINE_COLUMNS: {
  key: string;
  label: string;
  value: (row: ArRollForwardLine) => string | number | null | undefined;
  nowrap?: boolean;
  money?: boolean;
  numeric?: boolean;
  lines?: Line[];
}[] = [
  // A cash row has no claim, so its Clinic is blank.
  {
    key: 'clinic',
    label: 'Clinic',
    value: r => r.clinic_name ?? (r.claim_id ? 'No clinic' : null),
  },
  { key: 'claim', label: 'Claim', value: r => r.claim_number },
  { key: 'patient', label: 'Patient', value: r => r.patient_name },
  {
    key: 'service_period',
    label: 'Service period',
    value: r =>
      formatRemittanceDateRange(r.service_period_start, r.service_period_end),
    nowrap: true,
  },
  { key: 'payer', label: 'Payer', value: r => r.payer_name },
  {
    key: 'date',
    label: 'Date',
    value: r => formatRemittanceDate(r.event_date),
    nowrap: true,
  },
  { key: 'reason', label: 'Reason', value: r => r.reason },
  {
    key: 'age_days',
    label: 'Age (days)',
    value: r => r.age_days,
    numeric: true,
    lines: ['opening', 'closing'],
  },
  { key: 'amount', label: 'Amount', value: r => r.amount, money: true },
];
type LineColumn = (typeof LINE_COLUMNS)[number];
const lineKey = (c: LineColumn) => (c.money ? moneyKey(c.key) : c.key);
const lineCells = (row: ArRollForwardLine, columns = LINE_COLUMNS) =>
  Object.fromEntries(
    columns.map(c => [
      lineKey(c),
      c.money ? Number(c.value(row)) : c.value(row),
    ])
  );
const lineHeaders = (columns: LineColumn[]) =>
  Object.fromEntries(columns.map(c => [lineKey(c), c.label]));
const LINE_HEADERS = lineHeaders(LINE_COLUMNS);

const sheet = (
  name: string,
  headers: Record<string, string>,
  rows: Record<string, unknown>[]
): XlsxSheet => ({
  name,
  columns: Object.keys(headers),
  rows,
  columnLabels: new Map(Object.entries(headers)),
});

// Writes one sheet through the Report Builder's shared .xlsx export.
const downloadXlsx = async (
  fileName: string,
  headers: Record<string, string>,
  rows: Record<string, unknown>[],
  showError: (title: string, description?: string) => void
) => {
  try {
    await exportRowsToXlsx(
      fileName,
      Object.keys(headers),
      rows,
      new Map(Object.entries(headers))
    );
  } catch (error) {
    console.error('Failed to download the AR roll-forward', error);
    showError('AR roll-forward', 'Failed to download. Please try again.');
  }
};

function DrillRow({
  query,
  drill,
  span,
  onClose,
}: {
  query: LinesQuery;
  drill: Drill;
  span: number;
  onClose: () => void;
}) {
  const { label } = drill;
  const showError = useStore(s => s.showError);
  const columns = LINE_COLUMNS.filter(
    c => !c.lines || (!!query.line && c.lines.includes(query.line))
  );
  const linesQuery = useQuery({
    queryKey: [...billingKeys.all, 'ar-roll-forward-lines', query],
    queryFn: async () => {
      const response = await billingGetArRollForwardLines({ query });
      if (response.error || !response.data) {
        throw new Error(
          getUserFacingApiMessage(response, 'Failed to load the lines')
        );
      }
      return response.data;
    },
  });

  const download = () => {
    if (!linesQuery.data) return;
    const rows: Record<string, unknown>[] = linesQuery.data.rows.map(row =>
      lineCells(row, columns)
    );
    rows.push({
      [columns[0].key]: 'Lines total',
      [moneyKey('amount')]: Number(linesQuery.data.total),
    });
    const name = slug([drill.line, drill.month, ...drill.scope].join(' '));
    return downloadXlsx(
      `ar-roll-forward-${name}.xlsx`,
      lineHeaders(columns),
      rows,
      showError
    );
  };

  return (
    <tr>
      <td colSpan={span} className='bg-muted/30 p-2'>
        <section aria-label={`${label} lines`}>
          {/* Sticky so the heading stays in view when the table is scrolled. */}
          <div className='sticky left-0 mb-2 flex w-fit max-w-[calc(100vw-3rem)] flex-wrap items-center gap-2 md:max-w-none'>
            <h3 className='text-sm font-semibold'>
              {[drill.line, monthLabel(drill.month), ...drill.scope].join(
                ' · '
              )}
            </h3>
            <Button
              variant='outline'
              size='sm'
              disabled={!linesQuery.data}
              aria-label={`Download lines (.xlsx): ${label}`}
              onClick={download}
            >
              <Download className='h-4 w-4' />
              Download lines (.xlsx)
            </Button>
            <Button
              variant='ghost'
              size='sm'
              aria-label={`Close ${label} lines`}
              onClick={onClose}
            >
              <X className='h-4 w-4' />
              Close
            </Button>
          </div>
          {linesQuery.isError ? (
            <p className='text-sm text-red-700'>{linesQuery.error.message}</p>
          ) : !linesQuery.data ? (
            <p className='text-sm text-muted-foreground'>Loading…</p>
          ) : (
            <div className='overflow-auto'>
              <table className='w-full min-w-[640px] text-xs'>
                <thead>
                  <tr className='text-left text-muted-foreground'>
                    {columns.map(c => (
                      <th
                        key={c.key}
                        className={`px-2 py-1 ${c.money || c.numeric ? 'text-right' : ''}`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linesQuery.data.rows.map((row, index) => (
                    <tr key={index} className='border-t'>
                      {columns.map(c => (
                        <td
                          key={c.key}
                          className={
                            c.money || c.numeric
                              ? cellClass
                              : `px-2 py-1 ${c.nowrap ? 'whitespace-nowrap' : ''}`
                          }
                        >
                          {c.money
                            ? formatRemittanceCurrency(c.value(row)?.toString())
                            : (c.value(row) ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className='border-t font-semibold'>
                    <td colSpan={columns.length - 1} className='px-2 py-1'>
                      Lines total
                    </td>
                    <td className={cellClass}>
                      {formatRemittanceCurrency(linesQuery.data.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>
      </td>
    </tr>
  );
}

const ARRollForward: React.FC = () => {
  const canView = useStore(s => s.hasPermission(PermissionSlug.BILLING_ACCESS));
  const showError = useStore(s => s.showError);
  const [openKeys, setOpenKeys] = useState<Record<string, Drill>>({});
  const [pickedMonth, setPickedMonth] = useState<string>();
  const [downloading, setDownloading] = useState(false);
  const numberButtons = useRef<Record<string, HTMLButtonElement | null>>({});

  const reportQuery = useQuery({
    queryKey: [...billingKeys.all, 'ar-roll-forward'],
    queryFn: async () => {
      const response = await billingGetArRollForward();
      if (response.error || !response.data) {
        throw new Error(
          getUserFacingApiMessage(
            response,
            'Failed to load the AR roll-forward'
          )
        );
      }
      return response.data;
    },
    enabled: canView,
  });

  if (!canView) {
    return (
      <Alert className='border-red-200 bg-red-50'>
        <AlertDescription className='flex items-center gap-2 text-red-800'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          You do not have permission to view the AR roll-forward.
        </AlertDescription>
      </Alert>
    );
  }
  if (reportQuery.isError) {
    return <p className='text-sm text-red-700'>{reportQuery.error.message}</p>;
  }
  if (!reportQuery.data) {
    return <p className='text-sm text-muted-foreground'>Loading…</p>;
  }

  const { rows, month_totals, cash } = reportQuery.data;
  const months = month_totals
    .map(t => t.month)
    .sort()
    .reverse();
  const lastClosed = previousMonth(reportQuery.data.as_of);
  const selectedMonth =
    pickedMonth ?? (months.includes(lastClosed) ? lastClosed : months[0]);
  const shown = (month: string) =>
    selectedMonth === ALL_MONTHS || month === selectedMonth;

  const shownTotals = month_totals.filter(t => shown(t.month));
  const shownCash = cash.filter(c => shown(c.month));

  const rowsForMonth = (month: string) =>
    rows
      .filter(r => r.month === month)
      .map(row => ({
        row,
        clinic: row.clinic_name ?? 'No clinic',
        payer: PAYER_LABELS[row.payer_class],
      }));

  // Summary: the month table with its Total rows, a blank row, then the cash
  // block. Lines: every claim-keyed line of each shown month, one request each.
  const downloadReport = async () => {
    const figureCells = (figures: Figures) =>
      Object.fromEntries(
        COLUMNS.map(c => [moneyKey(c.line), Number(figures[c.line])])
      );
    const rowsOut: Record<string, unknown>[] = shownTotals.flatMap(total => [
      ...rowsForMonth(total.month).map(({ row, clinic, payer }) => ({
        month: monthLabel(row.month),
        clinic,
        payer,
        ...figureCells(row),
      })),
      {
        month: monthLabel(total.month),
        clinic: 'Total',
        ...figureCells(total),
      },
    ]);
    rowsOut.push(
      {},
      ...shownCash.map(c => ({
        month: monthLabel(c.month),
        clinic: 'Cash',
        ...Object.fromEntries(
          CASH_COLUMNS.map(k => [moneyKey(k.line), Number(c[k.line])])
        ),
      }))
    );
    const summaryHeaders = {
      month: 'Month',
      clinic: 'Clinic',
      payer: 'Payer',
      ...Object.fromEntries(COLUMNS.map(c => [moneyKey(c.line), c.label])),
      ...Object.fromEntries(CASH_COLUMNS.map(k => [moneyKey(k.line), k.label])),
    };
    setDownloading(true);
    try {
      const lineRows: Record<string, unknown>[] = [];
      for (const { month } of shownTotals) {
        const response = await billingGetArRollForwardLines({
          query: { month },
        });
        if (response.error || !response.data) {
          throw new Error(`Failed to load the lines of ${month}`);
        }
        lineRows.push(
          ...response.data.rows.map(row => ({
            type: LINE_LABELS[row.line] ?? row.line,
            month: monthLabel(month),
            payer_class: row.payer_class ? PAYER_LABELS[row.payer_class] : null,
            ...lineCells(row),
          }))
        );
      }
      await exportSheetsToXlsx(
        `ar-roll-forward-${(selectedMonth ?? ALL_MONTHS) === ALL_MONTHS ? 'all-months' : selectedMonth}.xlsx`,
        [
          sheet('Summary', summaryHeaders, rowsOut),
          sheet(
            'Lines',
            {
              type: 'Type',
              month: 'Month',
              payer_class: 'Payer class',
              ...LINE_HEADERS,
            },
            lineRows
          ),
        ]
      );
    } catch (error) {
      console.error('Failed to download the AR roll-forward', error);
      showError('AR roll-forward', 'Failed to download. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const toggle = (key: string, drill: Drill) =>
    setOpenKeys(prev => {
      const { [key]: existing, ...rest } = prev;
      return existing ? rest : { ...prev, [key]: drill };
    });

  // One number cell: a button that opens or closes its lines.
  const numberCell = (
    value: string,
    query: LinesQuery,
    drill: Drill,
    extra = ''
  ) => {
    const key = JSON.stringify(query);
    const open = key in openKeys;
    return (
      <td key={key} className={`${cellClass} ${extra}`}>
        <button
          type='button'
          ref={el => {
            numberButtons.current[key] = el;
          }}
          className={`-mx-1 rounded-sm px-1 underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${open ? 'bg-primary-50 ring-1 ring-primary-500' : ''}`}
          aria-label={`${drill.label}: ${formatRemittanceCurrency(value)}`}
          aria-expanded={open}
          onClick={() => toggle(key, drill)}
        >
          {formatRemittanceCurrency(value)}
        </button>
      </td>
    );
  };

  const drillRows = (queries: LinesQuery[], span: number) =>
    queries
      .filter(q => JSON.stringify(q) in openKeys)
      .map(q => {
        const key = JSON.stringify(q);
        return (
          <DrillRow
            key={key}
            query={q}
            drill={openKeys[key]}
            span={span}
            onClose={() => {
              toggle(key, openKeys[key]);
              // The Close button unmounts, so hand focus back to the number.
              numberButtons.current[key]?.focus();
            }}
          />
        );
      });

  const dataRow = (
    figures: Figures,
    month: string,
    where: { clinic?: string; payer_class?: ArRollForwardRow['payer_class'] },
    scope: string[],
    firstCells: React.ReactNode,
    rowClass = ''
  ) => {
    const queries = COLUMNS.map(c => ({ month, line: c.line, ...where }));
    return (
      <Fragment key={`${month}-${scope.join(' ')}`}>
        <tr className={`border-t ${rowClass}`}>
          {firstCells}
          {COLUMNS.map((c, i) =>
            numberCell(
              figures[c.line],
              queries[i],
              {
                label: `${c.label}, ${monthLabel(month)} ${scope.join(' ')}`,
                line: c.label,
                month,
                scope,
              },
              [
                c.memo ? 'text-muted-foreground' : '',
                c.line === 'unexplained' && Number(figures[c.line]) !== 0
                  ? 'bg-amber-100 font-semibold text-amber-900'
                  : '',
              ].join(' ')
            )
          )}
        </tr>
        {drillRows(queries, SPAN)}
      </Fragment>
    );
  };

  return (
    <div className='space-y-6'>
      <p className='text-sm text-muted-foreground'>
        Every month is recalculated when you open this page. A late remittance,
        a rejection or a repriced claim changes past months too. The current
        month includes every payment dated after it starts, so its numbers can
        change. Billed and balances use each claim's expected revenue, or its
        charges when it has none.
      </p>
      <div className='flex flex-wrap items-end gap-2'>
        <div className='w-full space-y-1 md:w-56'>
          <Label htmlFor='ar-roll-forward-month'>Month</Label>
          <Select
            value={selectedMonth ?? ALL_MONTHS}
            onValueChange={setPickedMonth}
          >
            <SelectTrigger
              id='ar-roll-forward-month'
              selectSize='sm'
              className='w-full'
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_MONTHS}>All months</SelectItem>
              {months.map(m => (
                <SelectItem key={m} value={m}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant='outline'
          size='sm'
          disabled={downloading}
          onClick={downloadReport}
        >
          <Download className='h-4 w-4' />
          Download report (.xlsx)
        </Button>
      </div>
      <div className='overflow-auto rounded-md border'>
        <table className='w-full min-w-[1100px] text-sm'>
          <thead>
            <tr className='bg-muted/40 text-left'>
              <th className='px-2 py-1'>Month</th>
              <th className='px-2 py-1'>Clinic</th>
              <th className='px-2 py-1'>Payer</th>
              {COLUMNS.map(c => (
                <th
                  key={c.line}
                  className={`px-2 py-1 text-right ${c.memo ? 'font-normal text-muted-foreground' : ''}`}
                >
                  {c.label}
                  {c.note ? (
                    <span className='block text-xs font-normal text-muted-foreground'>
                      {c.note}
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shownTotals.map(total => {
              const month = total.month;
              return (
                <Fragment key={month}>
                  {rowsForMonth(month).map(({ row, clinic, payer }) =>
                    dataRow(
                      row,
                      month,
                      {
                        clinic: row.clinic_name ?? 'none',
                        payer_class: row.payer_class,
                      },
                      [clinic, payer],
                      <>
                        <td className='px-2 py-1'>{monthLabel(month)}</td>
                        <td className='px-2 py-1'>{clinic}</td>
                        <td className='px-2 py-1'>{payer}</td>
                      </>
                    )
                  )}
                  {dataRow(
                    total,
                    month,
                    {},
                    ['Total'],
                    <>
                      <td className='px-2 py-1'>{monthLabel(month)}</td>
                      <td className='px-2 py-1' colSpan={2}>
                        Total
                      </td>
                    </>,
                    'bg-muted/40 font-semibold'
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className='overflow-auto rounded-md border'>
        <table className='w-full min-w-[480px] text-sm'>
          <thead>
            <tr className='bg-muted/40 text-left'>
              <th className='px-2 py-1'>Month</th>
              {CASH_COLUMNS.map(k => (
                <th key={k.line} className='px-2 py-1 text-right'>
                  {k.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shownCash.map(c => {
              const queries: LinesQuery[] = CASH_COLUMNS.map(k => ({
                month: c.month,
                line: k.line,
              }));
              return (
                <Fragment key={c.month}>
                  <tr className='border-t'>
                    <td className='px-2 py-1'>{monthLabel(c.month)}</td>
                    {CASH_COLUMNS.map((k, i) =>
                      numberCell(c[k.line], queries[i], {
                        label: `${k.label}, ${monthLabel(c.month)}`,
                        line: k.label,
                        month: c.month,
                        scope: ['Cash'],
                      })
                    )}
                  </tr>
                  {drillRows(queries, 3)}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ARRollForward;
